import { Component } from '@theme/component';

/**
 * Ingredient explorer
 *
 * A bubble chart and an accordion list driven by one selection. Opening a row
 * focuses its bubble; clicking a bubble opens its row. The bubbles are built
 * here from each item's data attributes for the same reason the tabs are: a
 * theme block's settings are not readable from the block that contains it.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} chart
 * @property {HTMLElement} [caption]
 * @property {HTMLElement[]} [items]
 *
 * @extends {Component<Refs>}
 */
class IngredientExplorer extends Component {
  requiredRefs = ['chart'];

  /** @type {HTMLButtonElement[]} */
  #bubbles = [];

  connectedCallback() {
    super.connectedCallback();

    this.#build();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);

    if (this.dataset.trigger === 'hover') {
      this.addEventListener('pointerover', this.#handleHover);
    }
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);

    this.classList.add('ingx--ready');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('pointerover', this.#handleHover);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  /** @returns {HTMLElement[]} */
  get items() {
    return this.refs.items || [];
  }

  get selectedIndex() {
    return this.items.findIndex((item) => item.dataset.selected === 'true');
  }

  /**
   * Open one item and focus its bubble. Passing the open item's own index
   * closes it, leaving nothing selected.
   *
   * @param {number} index
   */
  select(index) {
    const items = this.items;
    if (index < 0 || index >= items.length) return;

    const closing = this.selectedIndex === index;

    items.forEach((item, i) => {
      const isOpen = !closing && i === index;

      item.dataset.selected = isOpen ? 'true' : 'false';

      const header = item.querySelector('.ingx__header');
      const body = item.querySelector('.ingx__body');

      header?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      body?.toggleAttribute('hidden', !isOpen);
    });

    // The chart can skip items, so match on the stored item index rather than
    // on the bubble's position in the list.
    this.#bubbles.forEach((bubble) => {
      const isOpen = !closing && Number(bubble.dataset.index) === index;
      bubble.setAttribute('aria-pressed', isOpen ? 'true' : 'false');
    });

    this.#updateCaption(closing ? -1 : index);
  }

  #build() {
    const chart = this.refs.chart;
    chart.textContent = '';

    this.#bubbles = [];

    this.items.forEach((item, index) => {
      if (item.dataset.inChart !== 'true') return;

      const bubble = document.createElement('button');
      bubble.type = 'button';
      bubble.className = 'ingx__bubble';
      bubble.dataset.index = String(index);
      bubble.setAttribute('aria-pressed', 'false');
      bubble.style.setProperty('--bubble-size', `${item.dataset.bubbleSize || 30}%`);
      bubble.style.setProperty('--bubble-x', `${item.dataset.bubbleX || 50}%`);
      bubble.style.setProperty('--bubble-y', `${item.dataset.bubbleY || 50}%`);

      const image = item.dataset.image;
      if (image) bubble.style.backgroundImage = `url("${image.replace(/"/g, '%22')}")`;

      const number = item.dataset.number?.trim() || String(index + 1);
      const name = item.dataset.name?.trim() || `Item ${index + 1}`;

      bubble.setAttribute('aria-label', `${number} ${name}`);

      const label = document.createElement('span');
      label.className = 'ingx__bubble-number';
      label.textContent = number;
      bubble.append(label);

      chart.append(bubble);
      this.#bubbles.push(bubble);
    });

    const first = this.items.findIndex((item) => item.dataset.openByDefault === 'true');
    if (first !== -1) this.select(first);
    else this.#updateCaption(-1);
  }

  /** @param {number} index */
  #updateCaption(index) {
    const caption = this.refs.caption;
    if (!caption) return;

    const item = index === -1 ? null : this.items[index];

    if (!item) {
      caption.hidden = true;
      caption.textContent = '';
      return;
    }

    caption.hidden = false;
    caption.textContent = '';

    const eyebrow = document.createElement('span');
    eyebrow.className = 'ingx__caption-eyebrow';
    eyebrow.textContent = `${caption.dataset.prefix || ''} ${item.dataset.number || ''}`.trim();

    const name = document.createElement('span');
    name.className = 'ingx__caption-name';
    name.textContent = item.dataset.name || '';

    caption.append(eyebrow, name);

    const src = item.dataset.image;
    if (src) {
      const img = document.createElement('img');
      img.className = 'ingx__caption-image';
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      caption.prepend(img);
    }
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const bubble = target.closest('.ingx__bubble');
    if (bubble instanceof HTMLElement) {
      this.select(Number(bubble.dataset.index));
      return;
    }

    // Clicks inside the opened panel belong to whatever is in it.
    if (target.closest('.ingx__body')) return;
    if (target.closest('a, button, input, select, textarea, [role="button"]:not(.ingx__header)')) return;

    const item = target.closest('.ingx__item');
    if (!item) return;

    const index = this.items.indexOf(/** @type {HTMLElement} */ (item));
    if (index !== -1) this.select(index);
  };

  /** The row is a div, so Enter and Space have to be wired up by hand.
   *  @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.classList.contains('ingx__header')) return;

    event.preventDefault();

    const index = this.items.indexOf(/** @type {HTMLElement} */ (target.closest('.ingx__item')));
    if (index !== -1) this.select(index);
  };

  /** @param {PointerEvent} event */
  #handleHover = (event) => {
    // Touch fires pointerover just before click, which would select then
    // immediately toggle back off.
    if (event.pointerType !== 'mouse') return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('.ingx__bubble, .ingx__item');
    if (!trigger) return;

    const index = trigger.classList.contains('ingx__bubble')
      ? Number(/** @type {HTMLElement} */ (trigger).dataset.index)
      : this.items.indexOf(/** @type {HTMLElement} */ (trigger.closest('.ingx__item')));

    if (index !== -1 && index !== this.selectedIndex) this.select(index);
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const item = target.closest('.ingx__item');
    const index = this.items.indexOf(/** @type {HTMLElement} */ (item));
    if (index !== -1 && index !== this.selectedIndex) this.select(index);
  };
}

if (!customElements.get('ingredient-explorer')) {
  customElements.define('ingredient-explorer', IngredientExplorer);
}
